import { useState } from "react";
import db from "../db/database";
import {
    fetchInvoiceHistory,
    fetchFromBusinessCentral
} from "../utils/businessCentralUtils";
import {
    buildFilters,
    buildSyncMessage,
    mapInvoice,
    mapPostedInvoice,
    mapLine,
    mapPaymentDetails,
    mapLineCalculateVat,
    fetchLinesInChunks,
    filterEmptyDocuments
} from "../utils/syncUtils";
import { COUNTRY_CODES } from "../constants/countries"
import { refreshFinalDocumentNumbers } from "../services/invoiceSyncService";

export function useSyncHistoricos(handleHistoricosSynced, country) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const fetchAndMapInvoices = async (values) => {
        // No contabilizadas
        let filter = buildFilters(values, false);
        const invoiceHistory = await fetchInvoiceHistory(filter);

        // Contabilizadas
        filter = buildFilters(values, true);
        let isApiV2 = country.code === COUNTRY_CODES.HONDURAS || country.code === COUNTRY_CODES.ELSALVADOR; //Si es Honduras|Salvador API; OTROS: ODATA
        let postedSalesInvoice = isApiV2 ? "postedSalesInvoicesSant" : "PostedSalesInvoice";
        const postedInvoiceHistory = await fetchFromBusinessCentral(`${postedSalesInvoice}?${filter}`, '', isApiV2);

        // Map
        const mappedInvoices = invoiceHistory.map((obj) => mapInvoice(obj, country?.code));
        const mappedPostedInvoices = postedInvoiceHistory.map((obj) => mapPostedInvoice(obj, "Invoice", country?.code));

        // Lines
        const allLines = [];
        const paymentLines = [];

        if (mappedInvoices.length > 0) {
            const invoiceLines = await fetchLinesInChunks("LinesSalesInvoice", mappedInvoices, mapLineCalculateVat, 35, 'Document_No', true);

            allLines.push(...invoiceLines);

            const invoicePayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedInvoices,
                mapPaymentDetails,
                35,
                "Document_No_",
                true
            );
            paymentLines.push(...invoicePayments);
        }

        if (mappedPostedInvoices.length > 0) {
            const postedInvoiceLines = await fetchLinesInChunks("LinesPostedSalesInvoice", mappedPostedInvoices, mapLine);
            allLines.push(...postedInvoiceLines);

            const postedInvoicePayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedPostedInvoices,
                mapPaymentDetails,
                35,
                "Document_No_",
                true
            );
            paymentLines.push(...postedInvoicePayments);
        }

        return { mappedInvoices, mappedPostedInvoices, allLines, paymentLines };
    };

    const fetchAndMapCreditMemos = async (values) => {

        // No contabilizadas
        let filter = buildFilters(values, false);
        const creditMemoHistory = await fetchFromBusinessCentral(`SalesCreditMemo?${filter}`);

        // Contabilizadas
        filter = buildFilters(values, true);
        const postedCreditMemoHistory = await fetchFromBusinessCentral(`PostedSalesCreditMemo?${filter}`);

        // Map
        const mappedCreditMemos = creditMemoHistory.map((obj) => mapInvoice(obj, country?.code));
        const mappedPostedCreditMemos = postedCreditMemoHistory.map((obj) =>
            mapPostedInvoice(obj, "Credit Memo", country?.code)
        );

        // Lines
        const allLines = [];
        const paymentLines = [];

        if (mappedCreditMemos.length > 0) {
            const creditMemoLines = await fetchLinesInChunks("LinesSalesCreditMemo", mappedCreditMemos, mapLine, 35, 'Document_No', true);
            allLines.push(...creditMemoLines);

            const creditMemoPayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedCreditMemos,
                mapPaymentDetails,
                35,
                "Document_No_"
            );
            paymentLines.push(...creditMemoPayments);
        }

        if (mappedPostedCreditMemos.length > 0) {
            const postedCreditMemoLines = await fetchLinesInChunks("LinesPostedSalesCreditMemo", mappedPostedCreditMemos, mapLineCalculateVat);
            allLines.push(...postedCreditMemoLines);

            const postedCreditMemoPayments = await fetchLinesInChunks(
                "DetallePagoFactura",
                mappedPostedCreditMemos,
                mapPaymentDetails,
                35,
                "Document_No_",
                true
            );
            paymentLines.push(...postedCreditMemoPayments);
        }

        return { mappedCreditMemos, mappedPostedCreditMemos, allLines, paymentLines };
    };


    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            // Fetch data
            const {
                mappedInvoices,
                mappedPostedInvoices,
                allLines: invoiceLines,
                paymentLines: invoicePayments,
            } = await fetchAndMapInvoices(values);

            const {
                mappedCreditMemos,
                mappedPostedCreditMemos,
                allLines: creditMemoLines,
                paymentLines: creditMemoPayments,
            } = await fetchAndMapCreditMemos(values);

            const keys = {
                getLineKey: (d) => (d.posted === 1 ? d.No : d.Pre_Assigned_No),
                getPaymentKey: (d) => d.Pre_Assigned_No || d.No,
            };
            const inv = filterEmptyDocuments({ documents: [...mappedInvoices, ...mappedPostedInvoices], lines: invoiceLines, payments: invoicePayments, ...keys });
            const nc = filterEmptyDocuments({ documents: [...mappedCreditMemos, ...mappedPostedCreditMemos], lines: creditMemoLines, payments: creditMemoPayments, ...keys });

            // Replace stored data
            await db.invoices.clear();
            await db.invoices.bulkAdd([...inv.documents, ...nc.documents]);
            await db.invoiceLines.clear();
            await db.invoiceLines.bulkAdd([...inv.lines, ...nc.lines]);
            await db.invoicePaymentLines.clear();
            await db.invoicePaymentLines.bulkAdd([...inv.payments, ...nc.payments]);

            const count = (docs, p) => docs.filter((d) => d.posted === p).length;
            setSuccess(buildSyncMessage(count(inv.documents, 0), count(nc.documents, 0), count(inv.documents, 1), count(nc.documents, 1)));

            if (handleHistoricosSynced) {
                await handleHistoricosSynced();
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshCorrelatives = async (values) => {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            const refreshResult = await refreshFinalDocumentNumbers(country, values);
            const message = `Actualización de correlativos completada. Revisadas: ${refreshResult.totalCandidates}, actualizadas: ${refreshResult.updated}, sin número final: ${refreshResult.notFound}.`;
            setSuccess(message);

            if (handleHistoricosSynced) {
                await handleHistoricosSynced();
            }
        } catch (error) {
            setError(error.message || "No se pudieron actualizar los correlativos.");
        } finally {
            setLoading(false);
        }
    };


    return { loading, setLoading, error, success, setError, setSuccess, handleSubmit, handleRefreshCorrelatives };
}